import * as React from "react";
import { useRef, useState } from "react";

import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

import { ICD10Code } from "services/icd10-codes";
import getAlphabetLetterByIndex from "utils/getAlphabetLetterByIndex";

import {
  INACTIVE_ICD10_CHIP_SX,
  INACTIVE_ICD10_CODE_TOOLTIP,
  isInactiveICD10Code
} from "./icd10-inactive.constants";

interface DraggableICD10ChipProps {
  option: ICD10Code;
  index: number;
  total: number;
  disabled?: boolean;
  tagProps: object;
  moveChip: (dragIndex: number, hoverIndex: number) => void;
  highlightInactive?: boolean;
}

export default function DraggableICD10Chip({
  option,
  index,
  total,
  disabled = false,
  tagProps,
  moveChip,
  highlightInactive = false
}: DraggableICD10ChipProps) {
  const chipRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // MUI Autocomplete's root onMouseDown calls preventDefault() for any target
  // that isn't the input, which cancels the browser's native drag before it
  // can start. Stopping propagation here keeps the drag alive.
  const stopMouseDown = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleDragStart = (event: React.DragEvent) => {
    event.stopPropagation();
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
    if (chipRef.current) {
      event.dataTransfer.setDragImage(chipRef.current, 0, 0);
    }
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setIsDragOver(false);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    const dragIndex = Number(event.dataTransfer.getData("text/plain"));
    if (!Number.isNaN(dragIndex) && dragIndex !== index) {
      moveChip(dragIndex, index);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;
    if ((event.key === "ArrowLeft" || event.key === "ArrowUp") && index > 0) {
      event.preventDefault();
      event.stopPropagation();
      moveChip(index, index - 1);
    } else if (
      (event.key === "ArrowRight" || event.key === "ArrowDown") &&
      index < total - 1
    ) {
      event.preventDefault();
      event.stopPropagation();
      moveChip(index, index + 1);
    }
  };

  const dragHandle = (
    <Box
      component="span"
      onMouseDown={disabled ? undefined : stopMouseDown}
      onKeyDown={disabled ? undefined : handleKeyDown}
      role="button"
      aria-label={`Reorder code ${option.code}. Use arrow keys to move.`}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      data-testid={`drag_icd10_code_${option.code}`}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: "50%",
        outline: "none",
        cursor: disabled ? "default" : "grab",
        "&:focus-visible": {
          boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`
        }
      }}
    >
      <DragIndicatorIcon fontSize="small" />
    </Box>
  );

  const isInactive = highlightInactive && isInactiveICD10Code(option);

  return (
    <Tooltip title={isInactive ? INACTIVE_ICD10_CODE_TOOLTIP : ""}>
      <Chip
        ref={chipRef}
        color={isInactive ? "error" : "default"}
        draggable={!disabled}
        onMouseDown={disabled ? undefined : stopMouseDown}
        onDragStart={disabled ? undefined : handleDragStart}
        onDragEnd={disabled ? undefined : handleDragEnd}
        onDragOver={disabled ? undefined : handleDragOver}
        onDragLeave={disabled ? undefined : handleDragLeave}
        onDrop={disabled ? undefined : handleDrop}
        icon={disabled ? undefined : dragHandle}
        data-testid={`selected_icd10_code_${getAlphabetLetterByIndex(
          index
        )}_${option.code}`}
        label={`${getAlphabetLetterByIndex(index)}. ${option.code}: ${
          option?.description
        }`}
        sx={{
          cursor: disabled ? "default" : "grab",
          opacity: isDragging ? 0.4 : 1,
          ...(isDragOver && !disabled
            ? { outline: "2px dashed", outlineColor: "primary.main" }
            : {}),
          ...(isInactive ? INACTIVE_ICD10_CHIP_SX : {})
        }}
        {...tagProps}
      />
    </Tooltip>
  );
}
